/* eslint-disable @typescript-eslint/no-floating-promises, @typescript-eslint/prefer-nullish-coalescing, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getCurrentUserId, setEmailAddress } from "features/authSlice";
import { isPostLosLoan, setIsLanguageToggleLocked } from "features/preferredLanguageSlice";
import { useLoanSelectionRouter } from "hooks/useLoanSelectionRouter";
import { MyLoanInfo, useGetMyLoansQuery } from "pages/application/applicationService";
import { useNavigateToPostAuth } from "hooks/useNavigateToPostAuth";
import { ConsentTypes, useSetConsentMutation } from "pages/authentication/userService";
import { useGetBorrowerDetailsQuery } from "pages/dashboard/home/dashboardService";
import { ENCOMPASS_LOS_TYPE } from "utilities/loan-sidebar/constants";
import { getLoanKey } from "utilities/loan-sidebar/getLoanKey";
import LoansListV2 from "./loans-list/LoansListV2";

const pathsToShowMyLoans = ["/application", "/dashboard"];

const resolveLosType = (loan: MyLoanInfo): typeof ENCOMPASS_LOS_TYPE | "vesta" => {
  const raw = (loan as MyLoanInfo & { losType?: string }).losType;
  return raw === "vesta" ? "vesta" : ENCOMPASS_LOS_TYPE;
};

export default function MyLoansV2() {
  const userId = useSelector(getCurrentUserId);
  const { data, isLoading, isFetching } = useGetMyLoansQuery(userId, { skip: !userId });
  const [setConsents] = useSetConsentMutation();
  const location = useLocation();
  const dispatch = useDispatch();
  const routeLoanSelection = useLoanSelectionRouter();
  const navigateToPostAuth = useNavigateToPostAuth();

  const { data: userProfile } = useGetBorrowerDetailsQuery(userId, {
    skip: !userId,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!userProfile?.emailAddress) {
      return;
    }
    dispatch(setEmailAddress(userProfile.emailAddress));
  }, [userProfile?.emailAddress, dispatch]);

  const selectLoan = useCallback(
    (loan: MyLoanInfo) => {
      routeLoanSelection({
        loan,
        losType: resolveLosType(loan),
        loanKey: getLoanKey(loan),
        source: "my_loans_card",
      });
    },
    [routeLoanSelection],
  );

  useEffect(() => {
    if (isLoading || !data) {
      return;
    }

    dispatch(setIsLanguageToggleLocked(data.length > 0 && data.every((loan) => isPostLosLoan(loan.loanNumber))));

    data.forEach((myLoan) => {
      void setConsents({
        id: myLoan.nhDealApplicationId,
        consent: {
          userId,
          nhDealApplicationId: myLoan.nhDealApplicationId,
          prospectId: myLoan.prospectId,
          consentTypes: [ConsentTypes.PrivacyPolicy, ConsentTypes.TermsOfUse],
        },
      });
    });

    if (data.length === 1) {
      const previousPath =
        location.state != null && typeof location.state === "object" && "previousPath" in location.state
          ? (location.state as { previousPath: unknown }).previousPath
          : null;

      if (!pathsToShowMyLoans.some((path) => typeof previousPath === "string" && previousPath.includes(path))) {
        selectLoan(data[0]);
      }
    }
  }, [data, dispatch, isLoading, location.state, selectLoan, setConsents, userId]);

  const handleLoanCardClick = (index: number) => {
    if (data?.[index]) {
      selectLoan(data[index]);
    }
  };

  return <LoansListV2 data={data} isLoading={isLoading} isFetching={isFetching} onLoanCardClick={handleLoanCardClick} />;
}
